import { MarketSelector } from "@mindlercare/auth-ui";
import { CodeISO2 } from "@mindlercare/schema";
import { Icon, Render, Switch, Button } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { usePsychologistUser } from "hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatRelative, fromUnixTime, isBefore } from "date-fns";
import { getDateFnsLocale } from "components/i18nResources";
import { useUserHasRole } from "../../hooks/userHooks";
import {
  CloseButton,
  StandoutBannerMessage,
  StyledNavbarWrapper,
} from "./navbar.styles";
import { NavbarProps } from "./navbar.types";
import { IcbtNotifications } from "components/icbt/icbtNotifications";
import { styled } from "styled-components";
import { Typography } from "components/typography";

const TemporaryMarketSelector = () => {
  const isAdmin = useUserHasRole("Admin");

  const handleSelectMarket = (market: CodeISO2) => {
    localStorage.setItem("mindlercare:market", market);
    window.location.reload();
  };

  return (
    <MarketSelector
      canSelectMarket={isAdmin}
      handleMarketSelect={handleSelectMarket}
    />
  );
};

export const Navbar = ({
  nextPatientSlot,
  hideSensitiveInformation,
  handleNextMeetingClick,
  handleHideSensitiveInformation,
}: NavbarProps) => {
  const { t } = useTranslation();
  const icbtCommunicationEnabled = useFlag("ICBT_COMMUNICATION_MC_FEATURES");
  const startsAt = nextPatientSlot?.startsAt;
  const daysToMeeting = startsAt
    ? formatRelative(fromUnixTime(startsAt), new Date(), {
        locale: getDateFnsLocale(),
      })
    : undefined;

  const text =
    nextPatientSlot?.startsAt !== undefined &&
    nextPatientSlot?.endsAt !== undefined &&
    !isBefore(new Date(), fromUnixTime(nextPatientSlot.startsAt)) &&
    isBefore(new Date(), fromUnixTime(nextPatientSlot.endsAt))
      ? t("ONGOING_MEETING")
      : t("NEXT_MEETING_AT");

  const userPsychologistId = usePsychologistUser().psychologist?.psychologistId;

  const api = useMindlerApi();
  const { data: inactivePsychologist } = useQuery(
    api.psychologists.isInactive.queryOptions(
      {
        psychologistId: userPsychologistId as number,
      },
      { enabled: !!userPsychologistId },
    ),
  );

  const [shouldSuppressWarning, setShouldSuppressWarning] = useState(
    localStorage.getItem("suppressInactiveWarning") === "true",
  );
  const suppressInactiveWarning = () => {
    setShouldSuppressWarning(true);
    localStorage.setItem("suppressInactiveWarning", "true");
  };

  return (
    <StyledNavbarWrapper>
      {inactivePsychologist && !shouldSuppressWarning && (
        <StandoutBannerMessage>
          {t("INACTIVE_PSYCHOLOGIST")}
          <CloseButton onClick={suppressInactiveWarning}>
            <Icon.Close />
          </CloseButton>
        </StandoutBannerMessage>
      )}
      {nextPatientSlot && (
        <FlexHorizontal>
          {nextPatientSlot.method === "PHONE" ? <Icon.Phone /> : <Icon.Video />}{" "}
          <Typography insensitive variant="body-small">
            {text}
          </Typography>
          <Button variant="primary-light" onClick={handleNextMeetingClick}>
            {`${daysToMeeting?.toLowerCase()}`}
          </Button>
        </FlexHorizontal>
      )}
      <Render if={icbtCommunicationEnabled}>
        <IcbtNotifications />
      </Render>
      <FlexHorizontal>
        <Typography insensitive variant="body-small">
          {t("HIDE_SENSITIVE")}
        </Typography>
        <Switch
          isActive={hideSensitiveInformation}
          onClick={handleHideSensitiveInformation}
          id="cy-sensitive-data-toggle"
          mt={8}
        />
      </FlexHorizontal>
      <TemporaryMarketSelector />
    </StyledNavbarWrapper>
  );
};

const FlexHorizontal = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
